import * as React from 'react';
import b_ from 'b_';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import { compose } from 'recompose';
import translate, { InjectedTranslateProps } from 'shared/utils/translate';
import Filter from './components/Filter';
import Input from './components/Input';
import { selector, Data, responseToProps, StockFilterValuesQuery } from './selectors';
import { actions } from './actions';
import * as query from './query.gql';

import './index.scss';

export interface Props {}

type Enhanced = Props &
  InjectedTranslateProps &
  ReturnType<typeof selector> &
  typeof actions &
  Data;

const enhance = compose<Enhanced, Props>(
  translate,
  connect(selector, actions),
  graphql<Props & InjectedTranslateProps, StockFilterValuesQuery, {}, Data>(query, {
    props: responseToProps,
  })
);

const b = b_.with('stocks-filters');

class StockFiltersView extends React.Component<Enhanced> {
  render() {
    const {
      t,
      actualName,
      types,
      sectors,
      industries,
      capRanges,
      risks,
      priceChangePeriods,
      changeName,
      changeSector,
      changeIndustry,
      changeType,
      changeCapRange,
      changeRisk,
      changePriceChangePeriod,
      sort,
      sortDirection,
      sortedField,
      changePrice,
      prices,
    } = this.props;

    return (
      <ul className={b()}>
        <li className={b('filter', { type: 'name' })}>
          <Input isFirst value={actualName} onChange={changeName} />
        </li>
        <li className={b('filter', { type: 'stock-type' })}>
          <Filter
            shortTitle={t('Stocks type')}
            title={t('Stocks type')}
            options={types}
            onChange={changeType}
            actualIndex={this.getStockTypeIndex()}
          />
        </li>
        <li className={b('filter', { type: 'sector' })}>
          <Filter
            shortTitle={t('Stocks sector')}
            title={t('Stocks economics sector')}
            options={sectors}
            onChange={changeSector}
            actualIndex={this.getSectorIndex()}
          />
        </li>
        <li className={b('filter', { type: 'industry' })}>
          <Filter
            shortTitle={t('Stocks industry')}
            title={t('Stocks industry')}
            options={industries}
            onChange={changeIndustry}
            actualIndex={this.getIndustryIndex()}
          />
        </li>
        <li className={b('filter', { type: 'risk' })}>
          <Filter
            shortTitle={t('Risk')}
            title={t('Risk')}
            toRight
            options={risks}
            onChange={changeRisk}
            actualIndex={this.getRiskIndex()}
            actualSort={sortedField === 'risk' ? sortDirection : undefined}
            onSort={(order) => sort('risk', order)}
          />
        </li>
        <li className={b('filter', { type: 'capitalization' })}>
          <Filter
            shortTitle={t('Stocks company capitalization')}
            title={t('Stocks company capitalization')}
            options={capRanges}
            onChange={changeCapRange}
            actualIndex={this.getCapRangeIndex()}
            actualSort={sortedField === 'capitalization' ? sortDirection : undefined}
            onSort={(order) => sort('capitalization', order)}
          />
        </li>
        <li className={b('filter', { type: 'price' })}>
          <Filter
            shortTitle={t('Price')}
            title={t('Price')}
            toRight
            options={prices}
            onChange={changePrice}
            actualIndex={this.getPriceIndex()}
            actualSort={sortedField === 'price' ? sortDirection : undefined}
            onSort={(order) => sort('price', order)}
          />
        </li>
        <li className={b('filter', { type: 'price-change' })}>
          <Filter
            shortTitle={t('Stock pr change')}
            title={t('Stock pr change')}
            toRight
            isLast
            options={priceChangePeriods}
            onChange={changePriceChangePeriod}
            actualIndex={this.getPriceChangePeriodIndex()}
            actualSort={sortedField === 'priceChange' ? sortDirection : undefined}
            onSort={(order) => sort('priceChange', order)}
          />
        </li>
      </ul>
    );
  }

  private getSectorIndex() {
    const { sectors, actualSector } = this.props;
    if (!actualSector) {
      return 0;
    }
    return sectors.findIndex(({ value }) => value === actualSector);
  }

  private getIndustryIndex() {
    const { industries, actualIndustry } = this.props;
    if (!actualIndustry) {
      return 0;
    }
    return industries.findIndex(({ value }) => value === actualIndustry);
  }

  private getStockTypeIndex() {
    const { types, actualType } = this.props;

    const searchedIndex = types.findIndex(({ value }) => value === actualType);

    return searchedIndex;
  }

  private getCapRangeIndex() {
    const { capRanges, actualCapRange } = this.props;
    return capRanges.findIndex(
      ({ value }) => JSON.stringify(value) === JSON.stringify(actualCapRange)
    );
  }

  private getRiskIndex() {
    const { risks, actualRisk } = this.props;
    if (!actualRisk) {
      return 0;
    }
    return risks.findIndex(({ value }) => value === actualRisk);
  }

  private getPriceIndex() {
    const { prices, actualPrice } = this.props;
    if (!actualPrice) {
      return 0;
    }
    return prices.findIndex(({ value }) => value === actualPrice);
  }

  private getPriceChangePeriodIndex() {
    const { priceChangePeriods, actualPriceChangePeriod } = this.props;
    return priceChangePeriods.findIndex(({ value }) => value === actualPriceChangePeriod);
  }
}

export const StockFilters = enhance(StockFiltersView);
